import { SectionHeader } from '../common/section-header'
import { useEffect, useId, useMemo, useState } from 'react'
import type { Dispatch, ReactNode, SetStateAction } from 'react'
import { Link } from 'react-router-dom'
import {studyPeriodHref} from '../study-periods/navigation'
import { activityPageHref } from '../activity/activity-presentation'
import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query'

import {
  queriesStaleAfterEnrollmentChange,
  queryKeys,
} from '../api/query-keys'
import { AdminApiError } from '../api/client'
import { todayJstIso } from '../common/access-window'
import {
  OPERATOR_FIELD_GROUP,
  OPERATOR_FIELDSET_LEGEND,
} from '../common/form-layout'
import { DEFAULT_TROPHY_GOAL } from '../common/product-defaults'
import {
  ProgramPicker,
  usePrograms,
  ProgramName,
} from '../common/programs'
import { CohortPicker } from '../cohorts/cohort-picker'
import { cohortEnrollmentReadinessProblems } from '../cohorts/enrollment-readiness'
import { useCohort } from '../cohorts/use-cohort'
import { DisclosureChevron, Badge, Button, FieldLabel, Icon, Input, Notice, Select, SelectContent, SelectItem, SelectTrigger, SelectValue, Tooltip } from '@speaking-coach/ui'
import { adminStudentClient, messageForStudentError } from './api'
import {
  useOperatorFaq,
  OperatorFaqBadgeTrigger,
  OperatorFaqTrigger,
} from '../operator-faq/operator-faq'
import { CohortEnrollmentDefaultsPreview } from './cohort-enrollment-defaults-preview'
import type {
  CurriculumProgressRequirement,
  ContinuityBlockedReason,
  EnrollmentMutationResponse,
  EnrollmentRow,
  EnrollmentTermsBody,
  PlacementRequirement,
  ProductTier,
  StudentRow,
  TrophyProgressRequirement,
} from './types'

const TIERS: ProductTier[] = ['essential', 'pro', 'premium', 'unlimited']
const ENROLLMENT_ACTION_BUTTON_CLASS = 'bg-surface-card'

interface OperationFeedback {
  message: string
  tone: 'neutral' | 'success' | 'warning'
}

function addIsoDays(day: string, offset: number): string {
  const value = new Date(`${day}T00:00:00Z`)
  value.setUTCDate(value.getUTCDate() + offset)
  return value.toISOString().slice(0, 10)
}

function prettyDate(value: string | null): string {
  return value ?? 'not set'
}

function prettyTimestamp(value: string): string {
  return new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(new Date(value))
}

function recordedGoalDetail(
  reachedAt: string | null,
  trophyCount: number | null,
): string | null {
  if (reachedAt !== null) {
    return `Recorded ${prettyTimestamp(reachedAt)}${
      trophyCount === null ? '' : ` at ${trophyCount} trophies`
    }.`
  }
  return trophyCount === null ? null : `Recorded at ${trophyCount} trophies.`
}

function overlapsEnrollmentPeriod(
  enrollment: EnrollmentRow,
  proposedStart: string | null,
  proposedEnd: string | null,
): boolean {
  const occupiedEnd = enrollment.endedOn ?? enrollment.accessEndsOn
  return (
    enrollment.status !== 'cancelled' &&
    enrollment.scheduledStartOn !== null &&
    occupiedEnd !== null &&
    proposedStart !== null &&
    proposedEnd !== null &&
    enrollment.scheduledStartOn <= proposedEnd &&
    occupiedEnd >= proposedStart
  )
}

function policyLabel(value: string | null): string {
  return (value ?? 'not set').replaceAll('_', ' ')
}

function placementSourceLabel(source: 'diagnostic' | 'reused' | null): string | null {
  if (source === 'diagnostic') return 'New diagnostic'
  if (source === 'reused') return 'Inherited from prior enrollment'
  return null
}

function placementRequirementLabel(value: PlacementRequirement | null): string {
  return value === 'reuse_latest_result'
    ? 'Reuse the latest eligible result'
    : value === 'require_new_diagnostic'
      ? 'New diagnostic required'
      : 'Placement policy not set'
}

function curriculumRequirementLabel(
  value: CurriculumProgressRequirement | null,
): string {
  return value === 'continue_latest_progress'
    ? 'Continue prior curriculum progress'
    : value === 'start_new_attempt'
      ? 'Start a new curriculum attempt'
      : 'Curriculum policy not set'
}

function trophyRequirementLabel(
  value: TrophyProgressRequirement | null,
): string {
  return value === 'continue_latest_progress'
    ? 'Continue prior trophy progress'
    : value === 'start_new_progress'
      ? 'Start at zero trophies'
      : 'Trophy policy not set'
}

const CONTINUITY_BLOCKED_REASON: Record<ContinuityBlockedReason, string> = {
  source_enrollment_not_ended: 'The earlier enrollment has not ended yet.',
  source_call_active: 'A lesson is still active on the earlier enrollment.',
  source_post_call_fact_pending:
    'A completed lesson on the earlier enrollment is missing its final progress update.',
  source_post_call_job_pending:
    'A background learning update for the earlier enrollment is still running.',
  automatic_finalization_failed:
    'The automatic transfer hit a system error.',
}

function continuityBlockedReason(
  reason: ContinuityBlockedReason | null,
): string {
  return reason === null
    ? 'The earlier enrollment is not ready to transfer progress yet.'
    : CONTINUITY_BLOCKED_REASON[reason]
}

function continuityNeedsAttention(
  continuity: NonNullable<EnrollmentRow['continuity']>,
): boolean {
  return (
    continuity.status === 'blocked' ||
    (continuity.status === 'reserved' &&
      continuity.lastAutomaticErrorCode !== null &&
      continuity.consecutiveAutomaticFailureCount >= 3)
  )
}

function continuityStatusLabel(
  continuity: NonNullable<EnrollmentRow['continuity']>,
): string {
  if (continuity.status === 'reserved') {
    return continuity.lastAutomaticErrorCode !== null &&
      continuity.consecutiveAutomaticFailureCount >= 3
      ? 'Automatic transfer is retrying'
      : 'Waiting to preserve progress'
  }
  if (continuity.status === 'blocked') return 'Progress transfer delayed'
  if (continuity.status === 'finalized') return 'Progress preserved'
  if (continuity.status === 'cancelled') return 'Progress transfer cancelled'
  return 'Progress transfer expired'
}

function continuityStatusDetail(
  continuity: NonNullable<EnrollmentRow['continuity']>,
): string {
  if (continuity.status === 'reserved') {
    if (
      continuity.lastAutomaticErrorCode !== null &&
      continuity.consecutiveAutomaticFailureCount >= 3
    ) {
      return 'The automatic transfer has failed repeatedly and will retry again. You can try it now after checking the earlier enrollment.'
    }
    return 'The earlier enrollment is reserved. The system will transfer eligible progress automatically once it is safely settled.'
  }
  if (continuity.status === 'blocked') {
    return `${continuityBlockedReason(continuity.blockedReason)} Automatic retries continue.`
  }
  if (continuity.status === 'finalized') {
    return 'Eligible starting progress was copied into this enrollment. The earlier enrollment keeps its own history.'
  }
  if (continuity.status === 'cancelled') {
    return 'No progress was transferred, and the earlier enrollment is available to another eligible continuation.'
  }
  return 'This enrollment ended before progress could be transferred. No starting progress was created here.'
}

function continuitySourceProblem(
  source: EnrollmentRow,
  draft: DraftForm,
  plannedEarlyEnd: { enrollmentId: string; endedOn: string } | null = null,
): string | null {
  const sourceEffectiveEnd =
    plannedEarlyEnd?.enrollmentId === source.enrollmentId
      ? plannedEarlyEnd.endedOn
      : (source.endedOn ?? source.accessEndsOn)
  if (
    draft.scheduledStartOn !== '' &&
    (sourceEffectiveEnd === null ||
      sourceEffectiveEnd >= draft.scheduledStartOn)
  ) {
    return 'Its access period does not end before the new enrollment starts.'
  }
  if (
    draft.curriculumProgressRequirement === 'continue_latest_progress' &&
    source.programId !== draft.programId
  ) {
    return 'Its program does not match the new enrollment.'
  }
  return null
}

interface DraftForm {
  visibility: 'company' | 'private'
  cohortId: string
  scheduledStartOn: string
  accessEndsOn: string
  tier: ProductTier
  programId: string
  trophyGoal: string
  placementRequirement: PlacementRequirement
  curriculumProgressRequirement: CurriculumProgressRequirement
  trophyProgressRequirement: TrophyProgressRequirement
  sourceEnrollmentId: string
  externalPolicyRef: string
  replacementReason: string
}

type EnrollmentActionIntent = 'suspend' | 'cancel' | 'end' | 'replace'

interface EnrollmentActionExplanation {
  title: string
  description: string
  reasonLabel: string
  confirmLabel: string
  destructive: boolean
}

function explainEnrollmentAction(
  intent: EnrollmentActionIntent,
  status: EnrollmentRow['status'],
): EnrollmentActionExplanation {
  if (intent === 'suspend') {
    return {
      title: 'Suspend enrollment',
      description:
        "Place a temporary access hold when this enrollment should resume later. The student's dates, progress, and trophy history stay intact, but the access end date keeps running; suspension does not extend paid study time.",
      reasonLabel: 'Reason for suspension',
      confirmLabel: 'Confirm suspension',
      destructive: false,
    }
  }
  if (intent === 'cancel') {
    return {
      title: 'Cancel enrollment',
      description:
        'Cancel a scheduled period created by mistake or no longer expected to start. It remains in history and cannot grant study access; if participation has begun, end it instead.',
      reasonLabel: 'Reason for cancellation',
      confirmLabel: 'Confirm cancellation',
      destructive: true,
    }
  }
  if (intent === 'end') {
    return {
      title: 'End enrollment today',
      description:
        'Permanently end study access after today in Japan, such as for a withdrawal or an engagement ending early. The contractual end date stays recorded and this enrollment cannot be resumed.',
      reasonLabel: 'Reason for ending the enrollment',
      confirmLabel: 'Confirm end today',
      destructive: true,
    }
  }
  const active = status === 'active' || status === 'suspended'
  return {
    title: active ? 'End and replace enrollment' : 'Cancel and reschedule enrollment',
    description: active
      ? 'Close a current period when a distinct tier, course, ownership, or paid extension should take over next. The current enrollment ends after today in Japan and the replacement starts no earlier than tomorrow; both changes are saved together so the periods never overlap and the original terms remain unchanged.'
      : 'Replace a future period whose dates, cohort, or other contractual terms need to change. The unused period is cancelled and its replacement is created in the same operation.',
    reasonLabel: active
      ? 'Reason for ending and replacing the enrollment'
      : 'Reason for cancelling and rescheduling the enrollment',
    confirmLabel: active ? 'Continue to replacement' : 'Continue to reschedule',
    destructive: false,
  }
}

function newDraft(): DraftForm {
  return {
    visibility: 'company',
    cohortId: '',
    // Private enrollment dates are contractual terms. Do not suggest a
    // duration the operator might accidentally accept as a business default.
    scheduledStartOn: '',
    accessEndsOn: '',
    tier: 'pro',
    programId: '',
    trophyGoal: String(DEFAULT_TROPHY_GOAL),
    placementRequirement: 'require_new_diagnostic',
    curriculumProgressRequirement: 'start_new_attempt',
    trophyProgressRequirement: 'start_new_progress',
    sourceEnrollmentId: '',
    externalPolicyRef: '',
    replacementReason: '',
  }
}

function termsFromDraft(draft: DraftForm): EnrollmentTermsBody {
  const company = draft.visibility === 'company'
  const carrying =
    !company &&
    (draft.curriculumProgressRequirement === 'continue_latest_progress' ||
      draft.trophyProgressRequirement === 'continue_latest_progress')
  return {
    cohortId: company ? draft.cohortId : null,
    scheduledStartOn: company ? null : draft.scheduledStartOn,
    accessEndsOn: company ? null : draft.accessEndsOn,
    tier: company ? null : draft.tier,
    programId: company ? null : draft.programId.trim(),
    trophyGoal: company ? null : Number(draft.trophyGoal),
    placementRequirement: company
      ? 'require_new_diagnostic'
      : draft.placementRequirement,
    curriculumProgressRequirement: company
      ? 'start_new_attempt'
      : draft.curriculumProgressRequirement,
    trophyProgressRequirement: company
      ? 'start_new_progress'
      : draft.trophyProgressRequirement,
    externalPolicyRef: company
      ? null
      : draft.externalPolicyRef.trim() || null,
    continuitySourceEnrollmentId: carrying
      ? draft.sourceEnrollmentId
      : null,
    continuityCreationKey: carrying
      ? `operator:${crypto.randomUUID()}`
      : null,
  }
}

/** Refresh every read model derived from an enrollment, not just the cards. */
function invalidateEnrollmentViews(
  queryClient: QueryClient,
  student: Pick<StudentRow, 'clientId' | 'userId'>,
): void {
  for (const filters of queriesStaleAfterEnrollmentChange(
    student.clientId,
    student.userId,
  )) {
    void queryClient.invalidateQueries(filters)
  }
}

function enrollmentProgramLabel(enrollment: EnrollmentRow): string {
  const name = enrollment.program?.name.en ?? 'Draft enrollment'
  if (enrollment.initialProgramLevel) return `${name} · ${enrollment.initialProgramLevel.cefrLevel}`
  if (enrollment.status === 'cancelled') return name
  return `${name} · ${enrollment.status === 'ended' ? 'no placement recorded' : 'awaiting placement'}`
}

function unresolvedPlacementLabel(enrollment: EnrollmentRow): string {
  if (enrollment.status === 'cancelled') {
    return 'Not completed before cancellation'
  }
  if (enrollment.status === 'ended') {
    return 'Not completed during this enrollment'
  }
  return enrollment.placementRequirement === 'reuse_latest_result'
    ? 'Reusable result not available'
    : 'Awaiting diagnostic'
}

function defaultOpenEnrollmentIds(enrollments: EnrollmentRow[]): string[] {
  const byStartThenId = (left: EnrollmentRow, right: EnrollmentRow): number =>
    (left.scheduledStartOn ?? '9999-12-31').localeCompare(
      right.scheduledStartOn ?? '9999-12-31',
    ) || left.enrollmentId.localeCompare(right.enrollmentId)
  const active = enrollments
    .filter((enrollment) => enrollment.status === 'active')
    .sort(byStartThenId)[0]
  const suspended = enrollments
    .filter((enrollment) => enrollment.status === 'suspended')
    .sort(byStartThenId)[0]
  const pending = enrollments
    .filter((enrollment) => enrollment.status === 'pending')
    .sort(byStartThenId)[0]
  const focused = active ?? suspended ?? pending
  const needsAttention = enrollments
    .filter(
      (enrollment) =>
        enrollment.continuity !== null &&
        continuityNeedsAttention(enrollment.continuity),
    )
    .sort(byStartThenId)

  return [
    ...new Set([
      ...(focused === undefined ? [] : [focused.enrollmentId]),
      ...needsAttention.map((enrollment) => enrollment.enrollmentId),
    ]),
  ]
}

function EnrollmentCard({
  student,
  enrollment,
  onReplace,
  detailsOpen,
  onToggleDetails,
  onFocus,
}: {
  student: StudentRow
  enrollment: EnrollmentRow
  onReplace: (enrollment: EnrollmentRow, reason: string) => void
  detailsOpen: boolean
  onToggleDetails: () => void
  onFocus: () => void
}): JSX.Element {
  const queryClient = useQueryClient()
  const { openFaq } = useOperatorFaq()
  const detailsId = useId()
  const isActive = enrollment.status === 'active'
  const isCancelled = enrollment.status === 'cancelled'
  const placementLevel = enrollment.progressPreview?.placementLevel ?? null
  const practiceLevel = enrollment.chosenCefrLevel
    ?? enrollment.initialProgramLevel?.cefrLevel
    ?? null
  const placementSource = placementSourceLabel(
    enrollment.progressPreview?.placementSource ?? null,
  )
  const goalDetail =
    enrollment.progressPreview?.goalEvidenceStatus === 'reached'
      ? recordedGoalDetail(
          enrollment.progressPreview.goalReachedAt,
          enrollment.progressPreview.goalEvidenceTrophyCount,
        )
      : null
  const [feedback, setFeedback] = useState<OperationFeedback | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [reason, setReason] = useState('')
  const [actionIntent, setActionIntent] =
    useState<EnrollmentActionIntent | null>(null)

  const mutation = useMutation({
    mutationFn: (action: 'suspend' | 'resume' | 'cancel' | 'end') => {
      if (action === 'suspend')
        return adminStudentClient.suspendEnrollment(
          student.clientId,
          enrollment.enrollmentId,
          reason.trim(),
        )
      if (action === 'resume')
        return adminStudentClient.resumeEnrollment(
          student.clientId,
          enrollment.enrollmentId,
        )
      if (action === 'cancel')
        return adminStudentClient.cancelEnrollment(
          student.clientId,
          enrollment.enrollmentId,
          reason.trim(),
        )
      return adminStudentClient.earlyEndEnrollment(
        student.clientId,
        enrollment.enrollmentId,
        reason.trim(),
      )
    },
    onSuccess: (updated, action) => {
      setError(null)
      setActionIntent(null)
      setReason('')
      setFeedback({
        message:
          action === 'end'
            ? 'Period ended today; its contractual end remains unchanged.'
            : `Period ${updated.status}.`,
        tone: 'success',
      })
      invalidateEnrollmentViews(queryClient, student)
    },
    onError: (reason) => {
      setFeedback(null)
      setError(messageForStudentError(reason))
    },
  })

  const continuity = useMutation({
    mutationFn: () =>
      adminStudentClient.retryEnrollmentContinuity(
        student.clientId,
        enrollment.enrollmentId,
      ),
    onSuccess: (result) => {
      setError(null)
      setFeedback({
        message:
          result.outcome === 'finalized'
            ? 'Progress transfer completed. The new enrollment can now use the preserved progress.'
            : result.outcome === 'expired'
              ? 'The destination window expired and the source was released.'
              : `Progress transfer is still waiting. ${continuityBlockedReason(result.deferredReason)} Automatic retries will keep checking.`,
        tone:
          result.outcome === 'finalized'
            ? 'success'
            : result.outcome === 'deferred'
              ? 'warning'
              : 'neutral',
      })
      invalidateEnrollmentViews(queryClient, student)
    },
    onError: (reason) => {
      setFeedback(null)
      setError(messageForStudentError(reason))
    },
  })

  const busy = mutation.isPending || continuity.isPending
  const actionExplanation =
    actionIntent === null
      ? null
      : explainEnrollmentAction(actionIntent, enrollment.status)

  function beginAction(intent: EnrollmentActionIntent): void {
    setFeedback(null)
    setError(null)
    setReason('')
    setActionIntent(intent)
  }

  function confirmAction(): void {
    if (actionIntent === null || reason.trim() === '') return
    if (actionIntent === 'replace') {
      onReplace(enrollment, reason.trim())
      setActionIntent(null)
      setReason('')
      return
    }
    mutation.mutate(actionIntent)
  }

  return (
    <article
      aria-label={`${enrollment.status} enrollment: ${enrollmentProgramLabel(enrollment)}`}
      className={`space-y-3 rounded-token-card border p-4 ${
        isActive
          ? 'border-accent border-l-4 border-l-accent bg-surface-card shadow-raised'
          : isCancelled
            ? 'border-border bg-surface-app/70'
            : 'border-border'
      }`}
    >
      <header className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <strong className={isCancelled ? 'text-text-secondary' : undefined}>
              <Link to={studyPeriodHref(student.userId,enrollment.enrollmentId)} className={`underline underline-offset-2 ${isCancelled?'text-text-secondary':''}`}>
                {enrollmentProgramLabel(enrollment)}
              </Link>
            </strong>
            {isCancelled ? (
              <Badge variant="muted" className="bg-surface-card">
                <Icon name="cancel" size={12} /> Cancelled · unused
              </Badge>
            ) : (
              <Badge
                variant={
                  enrollment.status === 'active' ||
                  enrollment.status === 'pending'
                    ? 'neutral'
                    : 'muted'
                }
                className={
                  isActive
                    ? 'border-accent bg-accent-soft text-text-primary'
                    : undefined
                }
              >
                {enrollment.status}
              </Badge>
            )}
            <Badge variant="neutral">
              {enrollment.hrVisibilityScope === 'private'
                ? 'Private'
                : 'Company visible'}
            </Badge>
            {enrollment.isTrial && <Badge variant="muted">Trial</Badge>}
          </div>
          <p className="mt-1 flex items-center gap-1.5 text-body text-text-secondary">
            <Icon name="date" size={14} className="shrink-0" />
            {prettyDate(enrollment.scheduledStartOn)} →{' '}
            {prettyDate(enrollment.accessEndsOn)}
          </p>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="xs"
          aria-expanded={detailsOpen}
          aria-controls={detailsId}
          aria-label={`${detailsOpen ? 'Hide' : 'Show'} details for ${enrollmentProgramLabel(enrollment)}`}
          onClick={onToggleDetails}
        >
          <DisclosureChevron expanded={detailsOpen} direction="right" />
          {detailsOpen ? 'Hide details' : 'Show details'}
        </Button>
      </header>

      {detailsOpen && (
        <div id={detailsId} className="space-y-4">
          <dl
            aria-label="Enrollment terms"
            className="grid gap-x-6 gap-y-3 sm:grid-cols-2 lg:grid-cols-4"
          >
            <div>
              <dt className="text-caption text-text-secondary">Cohort</dt>
              <dd className="mt-0.5 text-body font-medium text-text-primary">
                {enrollment.cohortId !== null &&
                enrollment.cohortName !== null ? (
                  <Link
                    to={`/admin/clients/${student.clientId}/cohorts/${enrollment.cohortId}`}
                    className="underline underline-offset-2 hover:text-text-primary"
                  >
                    {enrollment.cohortName}
                  </Link>
                ) : (
                  'No cohort'
                )}
              </dd>
            </div>
            <div>
              <dt className="flex items-center gap-1.5 text-caption text-text-secondary">
                <Icon name="tier" size={14} /> Tier
              </dt>
              <dd className="mt-0.5 text-body font-medium capitalize text-text-primary">
                {enrollment.tier ?? 'Not set'}
              </dd>
            </div>
            <div>
              <dt className="text-caption text-text-secondary">Trophy goal</dt>
              <dd className="mt-0.5 text-body font-medium tabular-nums text-text-primary">
                {enrollment.trophyGoal ?? 'Not set'}
              </dd>
            </div>
            <div>
              <dt className="text-caption text-text-secondary">Participation</dt>
              <dd className="mt-0.5 text-body font-medium text-text-primary">
                {enrollment.participationStartedAt === null
                  ? 'Not started'
                  : 'Started'}
              </dd>
            </div>
          </dl>

          <section
            aria-label={isCancelled ? 'Planned learning setup' : 'Learning record'}
            className="border-t border-border-default pt-4"
          >
            <h3 className="mb-2 text-caption font-medium text-text-secondary">
              {isCancelled ? 'Planned learning setup' : 'Learning record'}
            </h3>
            <div className="grid gap-4 sm:grid-cols-3">
              <div>
                <div role="group" aria-label="Evaluated level">
                  <p className="flex items-center gap-1.5 text-caption font-medium text-text-secondary">
                    <Icon name="placement" size={14} /> Evaluated level
                  </p>
                  <p className="mt-2 text-body font-medium text-text-primary">
                    {placementLevel === null ? (
                      practiceLevel === null
                        ? unresolvedPlacementLabel(enrollment)
                        : 'No evaluated level recorded'
                    ) : (
                      <strong className="font-semibold">{placementLevel}</strong>
                    )}
                  </p>
                  <p className="mt-1 text-caption text-text-secondary">
                    {placementSource ??
                      placementRequirementLabel(enrollment.placementRequirement)}
                  </p>
                </div>
                {practiceLevel !== null ? (
                  <div role="group" aria-label="Practice level" className="mt-3">
                    <div className="flex items-center gap-1.5 text-caption font-medium text-text-secondary">
                      <span>Practice level</span>
                      <Tooltip
                        className="bg-surface-elev text-text-primary ring-1 ring-border-default [&_svg]:fill-surface-elev"
                        label="The level used for Home recommendations and summary topic counts: the learner’s choice, or the initial level if no choice is saved. Open the FAQ for details."
                      >
                        <Button
                          variant="ghost"
                          size="icon"
                          className="size-6 shrink-0 text-text-secondary"
                          aria-label="About practice level"
                          onClick={() => openFaq('practice-level')}
                        >
                          <Icon name="help" size={14} />
                        </Button>
                      </Tooltip>
                    </div>
                    <p className="mt-2 text-body font-medium text-text-primary">
                      <strong className="font-semibold">{practiceLevel}</strong>
                    </p>
                    <p className="mt-1 text-caption text-text-secondary">
                      {enrollment.chosenCefrLevel ? 'Learner’s choice' : 'Initial level'}
                    </p>
                  </div>
                ) : null}
              </div>
              <div
                role="group"
                aria-label="Curriculum"
              >
                <p className="flex items-center gap-1.5 text-caption font-medium text-text-secondary">
                  <Icon name="program" size={14} /> Curriculum
                </p>
                <p className="mt-2 text-body font-medium text-text-primary">
                  {enrollment.progressPreview === null
                    ? 'Progress not recorded'
                    : `${enrollment.progressPreview.practicedTopicCount} ${enrollment.progressPreview.practicedTopicCount === 1 ? 'topic' : 'topics'} practiced`}
                </p>
                <p className="mt-1 text-caption text-text-secondary">
                  {curriculumRequirementLabel(
                    enrollment.curriculumProgressRequirement,
                  )}
                </p>
              </div>
              <div
                role="group"
                aria-label="Trophies"
              >
                <p className="flex items-center gap-1.5 text-caption font-medium text-text-secondary">
                  <Icon name="trophy" size={14} /> Trophies
                </p>
                <p className="mt-2 text-body font-medium tabular-nums text-text-primary">
                  {enrollment.progressPreview === null
                    ? 'Progress not recorded'
                    : `${enrollment.progressPreview.earnedTrophyCount} of ${enrollment.trophyGoal ?? '—'} earned`}
                </p>
                <p className="mt-1 text-caption text-text-secondary">
                  {trophyRequirementLabel(enrollment.trophyProgressRequirement)}
                </p>
                {enrollment.progressPreview?.goalEvidenceStatus === 'reached' && (
                  <p className="mt-2 flex items-start gap-1.5 text-caption text-text-secondary">
                    <Icon
                      name="completed"
                      size={14}
                      className="mt-0.5 shrink-0 text-text-primary"
                    />
                    <span>
                      <strong className="font-semibold text-text-primary">
                        Goal reached.
                      </strong>
                      {goalDetail !== null ? ` ${goalDetail}` : ''}
                    </span>
                  </p>
                )}
                {enrollment.progressPreview?.goalEvidenceStatus === 'revoked' && (
                  <p className="mt-2 flex items-start gap-1.5 text-caption text-warning-text">
                    <Icon name="warning" size={14} className="mt-0.5 shrink-0" />
                    Completion evidence revoked; review its audit trail.
                  </p>
                )}
                {enrollment.progressPreview?.certificates.map((certificate) => (
                  <a
                    key={certificate.certificateId}
                    href={certificate.storageUri}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-2 flex items-center gap-1.5 text-caption font-medium text-text-primary underline decoration-border-strong underline-offset-2 hover:decoration-text-primary"
                  >
                    <Icon
                      name="certificate"
                      size={14}
                      className="text-text-primary"
                    />
                    Certificate · {certificate.locale.toUpperCase()} ·{' '}
                    {prettyTimestamp(certificate.issuedAt)}
                  </a>
                ))}
              </div>
            </div>
          </section>

          {(enrollment.endReason !== null || enrollment.continuity !== null) && (
            <dl
              aria-label="Enrollment audit details"
              className="grid gap-3 border-t border-border-default pt-3 text-body sm:grid-cols-2"
            >
              {enrollment.endReason !== null && (
                <div>
                  <dt className="text-caption text-text-secondary">End reason</dt>
                  <dd className="mt-0.5 text-text-primary">
                    {enrollment.endReason}
                  </dd>
                </div>
              )}
              {enrollment.continuity !== null && (
                <div className="sm:col-span-2">
                  <dt className="sr-only">Continuity status</dt>
                  <dd className="space-y-1 text-text-primary">
                    <div className="flex flex-wrap items-center gap-1">
                      <OperatorFaqBadgeTrigger
                        topicId="continuity-waiting"
                        label={continuityStatusLabel(enrollment.continuity)}
                        variant={
                          continuityNeedsAttention(enrollment.continuity)
                            ? 'danger'
                            : enrollment.continuity.status === 'cancelled' ||
                                enrollment.continuity.status === 'expired'
                              ? 'muted'
                              : 'neutral'
                        }
                      />
                    </div>
                    <p className="text-caption text-text-secondary">
                      {continuityStatusDetail(enrollment.continuity)}
                    </p>
                  </dd>
                </div>
              )}
            </dl>
          )}

          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap gap-2">
              {enrollment.status === 'active' && (
                <Button
                  type="button"
                  variant="warningOpen"
                  size="sm"
                  className={ENROLLMENT_ACTION_BUTTON_CLASS}
                  disabled={busy}
                  aria-expanded={actionIntent === 'suspend'}
                  onClick={() => {
                    onFocus()
                    beginAction('suspend')
                  }}
                >
                  <Icon name="pause" size={14} /> Suspend
                </Button>
              )}
              {enrollment.status === 'suspended' && (
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  className={ENROLLMENT_ACTION_BUTTON_CLASS}
                  disabled={busy}
                  onClick={() => {
                    onFocus()
                    mutation.mutate('resume')
                  }}
                >
                  <Icon name="play" size={14} /> Resume
                </Button>
              )}
              {(enrollment.status === 'draft' ||
                enrollment.status === 'pending') && (
                  <>
                    <Button
                      type="button"
                      variant="dangerOpen"
                      size="sm"
                      className={ENROLLMENT_ACTION_BUTTON_CLASS}
                      disabled={busy}
                      aria-expanded={actionIntent === 'cancel'}
                      onClick={() => {
                        onFocus()
                        beginAction('cancel')
                      }}
                    >
                      <Icon name="cancel" size={14} /> Cancel period
                    </Button>
                    <Button
                      type="button"
                      variant="dangerOpen"
                      size="sm"
                      className={ENROLLMENT_ACTION_BUTTON_CLASS}
                      disabled={busy}
                      aria-expanded={actionIntent === 'replace'}
                      onClick={() => {
                        onFocus()
                        beginAction('replace')
                      }}
                    >
                      <Icon name="refresh" size={14} /> Cancel and
                      reschedule
                    </Button>
                  </>
              )}
              {(enrollment.status === 'active' ||
                enrollment.status === 'suspended') && (
                  <>
                    <Button
                      type="button"
                      variant="dangerOpen"
                      size="sm"
                      className={ENROLLMENT_ACTION_BUTTON_CLASS}
                      disabled={busy}
                      aria-expanded={actionIntent === 'end'}
                      onClick={() => {
                        onFocus()
                        beginAction('end')
                      }}
                    >
                      <Icon name="endNow" size={14} /> End today
                    </Button>
                    <Button
                      type="button"
                      variant="dangerOpen"
                      size="sm"
                      className={ENROLLMENT_ACTION_BUTTON_CLASS}
                      disabled={busy}
                      aria-expanded={actionIntent === 'replace'}
                      onClick={() => {
                        onFocus()
                        beginAction('replace')
                      }}
                    >
                      <Icon name="refresh" size={14} /> End and
                      replace
                    </Button>
                  </>
              )}
              {enrollment.continuity !== null &&
                (enrollment.continuity.status === 'reserved' ||
                  enrollment.continuity.status === 'blocked') && (
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  className={ENROLLMENT_ACTION_BUTTON_CLASS}
                  disabled={busy}
                  onClick={() => {
                    onFocus()
                    setActionIntent(null)
                    setReason('')
                    setFeedback(null)
                    setError(null)
                    continuity.mutate()
                  }}
                >
                  <Icon name="refresh" size={14} /> Try transfer now
                </Button>
                )}
            </div>
            {/* Enrollment is its own audit subject (PROV-AUDIT). Keep this
                quiet because it opens the global Activity page, albeit
                already filtered to this exact enrollment. */}
            <Link
              to={activityPageHref(
                'enrollment',
                enrollment.enrollmentId,
                student.clientId,
              )}
              title="Open Activity filtered to this enrollment"
              className="ml-auto text-caption text-text-secondary underline decoration-border-strong underline-offset-2 hover:text-text-primary hover:decoration-current"
            >
              Activity log
            </Link>
          </div>
          {actionExplanation !== null && (
            <section
              aria-label={`${actionExplanation.title} details`}
              className="space-y-3"
            >
              <div>
                <h4 className="text-body font-medium">
                  {actionExplanation.title}
                </h4>
                <p className="mt-1 text-body text-text-secondary">
                  {actionExplanation.description}
                </p>
                <div className="mt-3 flex flex-wrap items-center gap-2">
                  <OperatorFaqTrigger
                    topicId="enrollment-lifecycle-actions"
                    label="Compare enrollment actions"
                  />
                  <OperatorFaqTrigger
                    topicId="student-access-states"
                    label="What will the student see?"
                  />
                </div>
              </div>
              <div>
                <FieldLabel
                  htmlFor={`enrollment-reason-${enrollment.enrollmentId}`}
                  label={actionExplanation.reasonLabel}
                  size="compact"
                  helpLabel="enrollment change reason"
                  help={
                    <>
                      Stored on the enrollment and in the audit trail. Use a
                      concise operational explanation that another operator
                      can understand; do not include sensitive personal
                      details.
                    </>
                  }
                />
                <Input
                  id={`enrollment-reason-${enrollment.enrollmentId}`}
                  autoFocus
                  className="bg-surface-card"
                  value={reason}
                  onChange={(event) => setReason(event.target.value)}
                  placeholder="Required"
                />
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  variant={
                    actionExplanation.destructive
                      ? 'dangerOpen'
                      : 'primary'
                  }
                  disabled={busy || reason.trim() === ''}
                  onClick={confirmAction}
                >
                  {actionExplanation.confirmLabel}
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  className={ENROLLMENT_ACTION_BUTTON_CLASS}
                  disabled={busy}
                  onClick={() => {
                    setActionIntent(null)
                    setReason('')
                  }}
                >
                  Keep enrollment unchanged
                </Button>
              </div>
            </section>
          )}
          {feedback?.tone === 'warning' && (
            <Notice variant="warning" role="status">
              {feedback.message}
            </Notice>
          )}
          {feedback !== null && feedback.tone !== 'warning' && (
            <p
              role="status"
              className={`flex items-center gap-1.5 text-body ${
                feedback.tone === 'success'
                  ? 'text-text-primary'
                  : 'text-text-secondary'
              }`}
            >
              {feedback.tone === 'success' && (
                <Icon name="success" className="shrink-0" />
              )}
              {feedback.message}
            </p>
          )}
          {error !== null && <Notice variant="danger">{error}</Notice>}
        </div>
      )}
    </article>
  )
}

export function EnrollmentOperationsSection({
  student,
}: {
  student: StudentRow
}): JSX.Element {
  const queryClient = useQueryClient()
  const [draft, setDraft] = useState<DraftForm>(newDraft)
  const [replacementSource, setReplacementSource] =
    useState<EnrollmentRow | null>(null)
  const [showForm, setShowForm] = useState(false)
  const [reviewing, setReviewing] = useState(false)
  const [result, setResult] = useState<EnrollmentMutationResponse | null>(null)
  const [openEnrollmentState, setOpenEnrollmentState] = useState<{
    studentId: string
    enrollmentIds: string[]
  } | null>(null)

  const query = useQuery({
    queryKey: queryKeys.studentEnrollments(student.clientId, student.userId),
    queryFn: () =>
      adminStudentClient.listEnrollments(student.clientId, student.userId),
  })
  const programs = usePrograms()
  const sourceOptions = useMemo(
    () =>
      (query.data?.enrollments ?? []).filter(
        (row) =>
          row.status === 'ended' ||
          row.status === 'active' ||
          row.status === 'suspended',
      ),
    [query.data],
  )
  const currentEnrollments = useMemo(
    () =>
      (query.data?.enrollments ?? []).filter(
        (row) => row.status !== 'ended' && row.status !== 'cancelled',
      ),
    [query.data],
  )
  const pastEnrollments = useMemo(
    () =>
      (query.data?.enrollments ?? []).filter(
        (row) => row.status === 'ended' || row.status === 'cancelled',
      ),
    [query.data],
  )
  const defaultOpenIds = useMemo(
    () => defaultOpenEnrollmentIds(query.data?.enrollments ?? []),
    [query.data],
  )
  const openEnrollmentIds =
    openEnrollmentState?.studentId === student.userId
      ? openEnrollmentState.enrollmentIds
      : defaultOpenIds
  const selectedCohort = useCohort(
    student.clientId,
    draft.visibility === 'company' ? draft.cohortId : '',
  ).data
  const selectedCohortProgramReady =
    selectedCohort !== undefined &&
    selectedCohort !== null &&
    programs.isSuccess &&
    programs.data.programs.some(
      (program) =>
        program.id === selectedCohort.defaultProgramId && program.readyForEnrollment,
    )
  const cohortProblems =
    selectedCohort === undefined || selectedCohort === null
      ? []
      : [
          ...cohortEnrollmentReadinessProblems(selectedCohort, todayJstIso()),
          ...(selectedCohort.defaultProgramId.trim() !== '' &&
          programs.isSuccess &&
          !selectedCohortProgramReady
            ? [
                'The cohort program is not ready for enrollment. Its diagnostic, default tutor, and lessons at every supported level must be available.',
              ]
            : []),
          ...(programs.isError
            ? [
                'Programs could not be verified. Reload before creating the enrollment.',
              ]
            : []),
        ]
  const proposedStart =
    draft.visibility === 'company'
      ? (selectedCohort?.scheduledStartOn ?? null)
      : draft.scheduledStartOn || null
  const proposedEnd =
    draft.visibility === 'company'
      ? (selectedCohort?.accessEndsOn ?? null)
      : draft.accessEndsOn || null
  const overlappingEnrollment = (query.data?.enrollments ?? []).find(
    (enrollment) =>
      enrollment.enrollmentId !== replacementSource?.enrollmentId &&
      overlapsEnrollmentPeriod(enrollment, proposedStart, proposedEnd),
  )

  const save = useMutation({
    mutationFn: () => {
      const terms = termsFromDraft(draft)
      if (replacementSource !== null) {
        const body = {
          reason: draft.replacementReason.trim(),
          replacement: terms,
        }
        return replacementSource.status === 'active' ||
          replacementSource.status === 'suspended'
          ? adminStudentClient.replaceEnrollment(
              student.clientId,
              replacementSource.enrollmentId,
              body,
            )
          : adminStudentClient.rescheduleEnrollment(
              student.clientId,
              replacementSource.enrollmentId,
              body,
            )
      }
      return adminStudentClient.createEnrollment(student.clientId, {
        ...terms,
        userId: student.userId,
      })
    },
    onSuccess: (created) => {
      setResult(created)
      setOpenEnrollmentState({
        studentId: student.userId,
        enrollmentIds: [created.enrollment.enrollmentId],
      })
      setShowForm(false)
      setReviewing(false)
      setReplacementSource(null)
      setDraft(newDraft())
      invalidateEnrollmentViews(queryClient, student)
    },
  })

  const carrying =
    draft.curriculumProgressRequirement === 'continue_latest_progress' ||
    draft.trophyProgressRequirement === 'continue_latest_progress'
  const programIdAvailable =
    programs.data?.programs.some(
      (program) => program.id === draft.programId && program.readyForEnrollment,
    ) ?? false
  const replacementKind =
    replacementSource === null
      ? 'create'
      : replacementSource.status === 'active' ||
          replacementSource.status === 'suspended'
        ? 'end'
        : 'reschedule'
  const plannedEarlyEnd =
    replacementKind === 'end' && replacementSource !== null
      ? {
          enrollmentId: replacementSource.enrollmentId,
          endedOn: todayJstIso(),
        }
      : null
  const selectedSource = sourceOptions.find(
    (source) => source.enrollmentId === draft.sourceEnrollmentId,
  )
  const reusablePlacementSource = sourceOptions.find(
    (source) => {
      const sourceEffectiveEnd =
        plannedEarlyEnd?.enrollmentId === source.enrollmentId
          ? plannedEarlyEnd.endedOn
          : (source.endedOn ?? source.accessEndsOn)
      return (
        source.progressPreview?.placementLevel !== null &&
        source.progressPreview?.placementLevel !== undefined &&
        sourceEffectiveEnd !== null &&
        draft.scheduledStartOn !== '' &&
        sourceEffectiveEnd < draft.scheduledStartOn
      )
    },
  )
  const reusablePlacementLevel =
    reusablePlacementSource?.progressPreview?.placementLevel ?? null
  const placementReuseAvailable = reusablePlacementLevel !== null
  useEffect(() => {
    if (placementReuseAvailable) return
    setDraft((current) => {
      if (
        current.placementRequirement !== 'reuse_latest_result' &&
        current.curriculumProgressRequirement !== 'continue_latest_progress' &&
        current.trophyProgressRequirement !== 'continue_latest_progress'
      ) {
        return current
      }
      return {
        ...current,
        placementRequirement: 'require_new_diagnostic',
        curriculumProgressRequirement: 'start_new_attempt',
        trophyProgressRequirement: 'start_new_progress',
      }
    })
  }, [placementReuseAvailable])
  const continuitySourceCompatible =
    !carrying ||
    (selectedSource !== undefined &&
      continuitySourceProblem(selectedSource, draft, plannedEarlyEnd) === null)
  const companyDefaultsComplete =
    selectedCohort !== undefined &&
    selectedCohort !== null &&
    cohortProblems.length === 0 &&
    programs.isSuccess &&
    selectedCohortProgramReady
  const earliestPrivateStart =
    replacementKind === 'end'
      ? addIsoDays(todayJstIso(), 1)
      : todayJstIso()
  const privateTermsComplete =
    draft.scheduledStartOn >= earliestPrivateStart &&
    draft.accessEndsOn >= draft.scheduledStartOn &&
    programIdAvailable &&
    Number(draft.trophyGoal) > 0 &&
    (draft.placementRequirement !== 'reuse_latest_result' ||
      placementReuseAvailable) &&
    continuitySourceCompatible
  const valid =
    (replacementSource === null || draft.replacementReason.trim() !== '') &&
    (draft.visibility === 'company'
      ? draft.cohortId !== '' && companyDefaultsComplete
      : privateTermsComplete)
  const actionLabel =
    replacementKind === 'create'
      ? 'Create enrollment'
      : replacementKind === 'end'
        ? 'End and create replacement'
        : 'Cancel and create replacement'

  function openNewEnrollment(): void {
    save.reset()
    setResult(null)
    setDraft(newDraft())
    setReplacementSource(null)
    setReviewing(false)
    setShowForm(true)
  }

  function closeForm(): void {
    save.reset()
    setShowForm(false)
    setReviewing(false)
    setReplacementSource(null)
  }

  return (
    <section
      aria-label="Enrollment periods"
      className="space-y-4 rounded-token-card bg-surface-card p-6"
    >
      <SectionHeader
        title="Enrollment periods"
        description={"Renewals and returns after a gap create independent periods. Moving a student to another tier also requires a new period. Existing credentials and earlier history are never reopened."}
        actions={<Button type="button" variant="primary" onClick={openNewEnrollment}>
          <Icon name="newEnrollment" /> Add enrollment
        </Button>}
      />

      {result !== null && (
        <Notice variant="neutral" role="status">
          Enrollment created.{' '}
          {result.notificationState === 'existing_activation'
            ? 'The existing invitation email keeps its original enrollment and send schedule. No new invitation email was queued.'
            : 'A new email is queued for this enrollment’s invitation schedule.'}
        </Notice>
      )}

      {showForm && (
        <div className={OPERATOR_FIELD_GROUP}>
          <div>
            <SectionHeader
              level={3}
              title={reviewing
                ? 'Review enrollment change'
                : replacementKind === 'create'
                  ? 'New enrollment'
                  : replacementKind === 'end'
                    ? 'End and replace'
                    : 'Cancel and reschedule'}
            />
            {replacementSource !== null && (
              <div className="mt-1 space-y-1 text-body text-text-secondary">
                <p>
                  {replacementKind === 'end'
                    ? 'The current period ends today and the replacement starts tomorrow or later.'
                    : 'The unused period is cancelled and its shifted replacement is created.'}{' '}
                  Both changes commit together.
                </p>
                <p>Reason: {draft.replacementReason}</p>
              </div>
            )}
          </div>

          {reviewing ? (
            <EnrollmentReview
              draft={draft}
              cohort={selectedCohort ?? null}
              replacementSource={replacementSource}
            />
          ) : (
            <>
              <div>
                <FieldLabel
                  htmlFor="enrollment-visibility"
                  label="Visibility"
                  help="Company visibility requires a cohort and lets only the HR managers assigned to that cohort see this period. Private periods remain visible only to the student and Cooori operators."
                />
                <Select
                  value={draft.visibility}
                  onValueChange={(value: 'company' | 'private') =>
                    setDraft((current) => ({
                      ...current,
                      visibility: value,
                      cohortId: value === 'company' ? current.cohortId : '',
                      sourceEnrollmentId:
                        value === 'company' ? '' : current.sourceEnrollmentId,
                      curriculumProgressRequirement:
                        value === 'company'
                          ? 'start_new_attempt'
                          : current.curriculumProgressRequirement,
                      trophyProgressRequirement:
                        value === 'company'
                          ? 'start_new_progress'
                          : current.trophyProgressRequirement,
                    }))
                  }
                >
                  <SelectTrigger id="enrollment-visibility">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="private">
                      Private — student and Cooori only
                    </SelectItem>
                    <SelectItem value="company">
                      Company — controlled by cohort HR access
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {draft.visibility === 'company' ? (
                <div>
                  <FieldLabel
                    htmlFor="enrollment-cohort"
                    label="Cohort"
                    help="The cohort supplies the access dates, tier, program, trophy goal, and HR visibility. Those values are copied into this enrollment when you confirm it; later cohort edits do not rewrite this period."
                  />
                  <CohortPicker
                    id="enrollment-cohort"
                    label="Cohort"
                    clientId={student.clientId}
                    value={draft.cohortId}
                    onChange={(cohortId) =>
                      setDraft((current) => ({ ...current, cohortId }))
                    }
                    emptyLabel="Choose a cohort"
                    requireCompleteEnrollmentDefaults
                  />
                  {selectedCohort !== undefined &&
                    selectedCohort !== null && (
                      <CohortEnrollmentDefaultsPreview cohort={selectedCohort} />
                    )}
                  {selectedCohort !== undefined &&
                    selectedCohort !== null &&
                    !companyDefaultsComplete && (
                      <Notice variant="warning" className="mt-3">
                        <p>This cohort cannot accept an enrollment yet:</p>
                        <ul className="mt-1 list-disc space-y-0.5 pl-5">
                          {cohortProblems.map((problem) => (
                            <li key={problem}>{problem}</li>
                          ))}
                        </ul>
                      </Notice>
                    )}
                </div>
              ) : (
                <PrivateTermsFields
                  draft={draft}
                  setDraft={setDraft}
                  earliestStart={earliestPrivateStart}
                />
              )}

              {draft.visibility === 'private' && (
                <>
                  <ContinuityFields
                    draft={draft}
                    setDraft={setDraft}
                    carrying={carrying}
                    sourceOptions={sourceOptions}
                    reusablePlacementLevel={reusablePlacementLevel}
                    plannedEarlyEnd={plannedEarlyEnd}
                  />
                  <ExternalPolicyReferenceField
                    draft={draft}
                    setDraft={setDraft}
                  />
                </>
              )}

            </>
          )}

          {save.error !== null && (
            <Notice variant="danger">
              <div className="space-y-1">
                <p>{messageForStudentError(save.error)}</p>
                {save.error instanceof AdminApiError &&
                  save.error.detail === 'period_overlap' &&
                  replacementSource === null &&
                  (overlappingEnrollment?.status === 'active' ||
                    overlappingEnrollment?.status === 'suspended') && (
                    <p>
                      If this period should replace the current one, close this
                      form and use <strong>End and replace</strong> on that
                      enrollment. It performs both changes atomically.
                      Otherwise, choose dates after the current period ends.
                    </p>
                  )}
                {save.error instanceof AdminApiError &&
                  save.error.detail === 'period_overlap' &&
                  replacementSource === null &&
                  (overlappingEnrollment?.status === 'draft' ||
                    overlappingEnrollment?.status === 'pending') && (
                    <p>
                      If this is a correction to the unused future period,
                      close this form and use{' '}
                      <strong>Cancel and reschedule</strong> on that enrollment.
                      Otherwise, choose dates that do not overlap it.
                    </p>
                  )}
                {save.error instanceof AdminApiError &&
                  save.error.detail === 'period_overlap' &&
                  overlappingEnrollment === undefined && (
                    <p>
                      Choose dates after the existing enrollment ends, then
                      review the change again.
                    </p>
                  )}
              </div>
            </Notice>
          )}

          <div className="flex flex-wrap gap-2">
            {reviewing ? (
              <>
                <Button
                  type="button"
                  disabled={save.isPending}
                  onClick={() => save.mutate()}
                >
                  {save.isPending ? 'Saving…' : `Confirm: ${actionLabel}`}
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  disabled={save.isPending}
                  onClick={() => setReviewing(false)}
                >
                  Back to edit
                </Button>
              </>
            ) : (
              <Button
                type="button"
                disabled={!valid}
                onClick={() => setReviewing(true)}
              >
                Review change
              </Button>
            )}
            <Button
              type="button"
              variant="secondary"
              disabled={save.isPending}
              onClick={closeForm}
            >
              Close
            </Button>
          </div>
        </div>
      )}

      {query.isLoading && (
        <p className="text-body text-text-secondary">
          Loading enrollment periods…
        </p>
      )}
      {query.error !== null && (
        <Notice variant="danger">
          {messageForStudentError(query.error)}
        </Notice>
      )}
      {query.data?.enrollments.length === 0 && (
        <p className="text-body text-text-secondary">
          No enrollment period exists yet. Add the first enrollment period.
        </p>
      )}
      {currentEnrollments.length > 0 && (
        <EnrollmentGroup
          title="Current and upcoming"
          description="Periods that can still affect access or require an operator action."
          enrollments={currentEnrollments}
          student={student}
          openEnrollmentIds={openEnrollmentIds}
          onToggleDetails={(enrollmentId) => {
            setOpenEnrollmentState({
              studentId: student.userId,
              enrollmentIds: openEnrollmentIds.includes(enrollmentId)
                ? openEnrollmentIds.filter((value) => value !== enrollmentId)
                : [enrollmentId],
            })
          }}
          onFocus={(enrollmentId) => {
            setOpenEnrollmentState({
              studentId: student.userId,
              enrollmentIds: [enrollmentId],
            })
          }}
          onReplace={(source, reason) => {
            save.reset()
            setResult(null)
            setDraft({ ...newDraft(), replacementReason: reason })
            setReplacementSource(source)
            setReviewing(false)
            setShowForm(true)
          }}
        />
      )}
      {pastEnrollments.length > 0 && (
        <EnrollmentGroup
          title="Past enrollments"
          enrollments={pastEnrollments}
          student={student}
          openEnrollmentIds={openEnrollmentIds}
          onToggleDetails={(enrollmentId) => {
            setOpenEnrollmentState({
              studentId: student.userId,
              enrollmentIds: openEnrollmentIds.includes(enrollmentId)
                ? openEnrollmentIds.filter((value) => value !== enrollmentId)
                : [enrollmentId],
            })
          }}
          onFocus={(enrollmentId) => {
            setOpenEnrollmentState({
              studentId: student.userId,
              enrollmentIds: [enrollmentId],
            })
          }}
          onReplace={(source, reason) => {
            save.reset()
            setResult(null)
            setDraft({ ...newDraft(), replacementReason: reason })
            setReplacementSource(source)
            setReviewing(false)
            setShowForm(true)
          }}
        />
      )}
    </section>
  )
}

function EnrollmentGroup({
  title,
  description,
  enrollments,
  student,
  onReplace,
  openEnrollmentIds,
  onToggleDetails,
  onFocus,
}: {
  title: string
  description?: string
  enrollments: EnrollmentRow[]
  student: StudentRow
  onReplace: (enrollment: EnrollmentRow, reason: string) => void
  openEnrollmentIds: string[]
  onToggleDetails: (enrollmentId: string) => void
  onFocus: (enrollmentId: string) => void
}): JSX.Element {
  return (
    <section aria-label={title} className="space-y-3">
      <div>
        <SectionHeader level={3} title={title} />
        {description !== undefined && (
          <p className="mt-0.5 text-caption text-text-secondary">{description}</p>
        )}
      </div>
      {enrollments.map((enrollment) => (
        <EnrollmentCard
          key={enrollment.enrollmentId}
          student={student}
          enrollment={enrollment}
          onReplace={onReplace}
          detailsOpen={openEnrollmentIds.includes(enrollment.enrollmentId)}
          onToggleDetails={() => onToggleDetails(enrollment.enrollmentId)}
          onFocus={() => onFocus(enrollment.enrollmentId)}
        />
      ))}
    </section>
  )
}

function PrivateTermsFields({
  draft,
  setDraft,
  earliestStart,
}: {
  draft: DraftForm
  setDraft: Dispatch<SetStateAction<DraftForm>>
  earliestStart: string
}): JSX.Element {
  return (
    <div className="space-y-3">
      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <FieldLabel
            htmlFor="enrollment-start"
            label="Access starts"
            help="Choose the agreed first day in Japan. For an active-period replacement, the earliest permitted date is tomorrow so the periods never overlap."
          />
          <Input
            id="enrollment-start"
            type="date"
            min={earliestStart}
            value={draft.scheduledStartOn}
            onChange={(event) =>
              setDraft((current) => ({
                ...current,
                scheduledStartOn: event.target.value,
              }))
            }
          />
        </div>
        <div>
          <FieldLabel
            htmlFor="enrollment-end"
            label="Access ends"
            help="Choose the agreed final day. Access ends after this Japan business day. A renewal is a new enrollment; do not stretch this date to combine distinct paid periods."
          />
          <Input
            id="enrollment-end"
            type="date"
            min={draft.scheduledStartOn || earliestStart}
            value={draft.accessEndsOn}
            onChange={(event) =>
              setDraft((current) => ({
                ...current,
                accessEndsOn: event.target.value,
              }))
            }
          />
        </div>
      </div>
      <div className="grid gap-3 sm:grid-cols-3">
        <div>
          <FieldLabel
            htmlFor="enrollment-tier"
            label="Tier"
            help="The product entitlement for this new period. Existing enrollments cannot be retiered; use End & replace to move the student to another tier without rewriting history."
          />
          <Select
            value={draft.tier}
            onValueChange={(tier: ProductTier) =>
              setDraft((current) => ({ ...current, tier }))
            }
          >
            <SelectTrigger id="enrollment-tier">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TIERS.map((tier) => (
                <SelectItem key={tier} value={tier}>
                  {tier}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <FieldLabel
            htmlFor="enrollment-program"
            label="Program"
            help="The Program this period opens. Placement resolves its initial level. Continuing curriculum progress requires the same Program as the earlier enrollment."
          />
          <ProgramPicker
            requireReady
            id="enrollment-program"
            value={draft.programId}
            onValueChange={(programId) =>
              setDraft((current) => ({
                ...current,
                programId,
              }))
            }
          />
        </div>
        <div>
          <FieldLabel
            htmlFor="enrollment-goal"
            label="Trophy goal"
            help="The completion gate for this enrollment. It may support reimbursement, mandatory training, or a personal goal, but the funding arrangement itself is not stored here."
          />
          <Input
            id="enrollment-goal"
            type="number"
            min="1"
            step="1"
            value={draft.trophyGoal}
            onChange={(event) =>
              setDraft((current) => ({
                ...current,
                trophyGoal: event.target.value,
              }))
            }
          />
        </div>
      </div>
    </div>
  )
}

function ExternalPolicyReferenceField({
  draft,
  setDraft,
}: {
  draft: DraftForm
  setDraft: Dispatch<SetStateAction<DraftForm>>
}): JSX.Element {
  return (
    <div>
      <FieldLabel
        htmlFor="external-policy-ref"
        label="External policy reference (optional)"
        help="An opaque CRM, contract, purchase order, or policy identifier. Cooori stores it for traceability but never interprets it or uses it to decide access, payment, or completion."
      />
      <Input
        id="external-policy-ref"
        value={draft.externalPolicyRef}
        onChange={(event) =>
          setDraft((current) => ({
            ...current,
            externalPolicyRef: event.target.value,
          }))
        }
      />
    </div>
  )
}

function ContinuityFields({
  draft,
  setDraft,
  carrying,
  sourceOptions,
  reusablePlacementLevel,
  plannedEarlyEnd,
}: {
  draft: DraftForm
  setDraft: Dispatch<SetStateAction<DraftForm>>
  carrying: boolean
  sourceOptions: EnrollmentRow[]
  reusablePlacementLevel: string | null
  plannedEarlyEnd: { enrollmentId: string; endedOn: string } | null
}): JSX.Element {
  const selectedSource = sourceOptions.find(
    (source) => source.enrollmentId === draft.sourceEnrollmentId,
  )
  const selectedSourceProblem =
    selectedSource === undefined
      ? null
      : continuitySourceProblem(selectedSource, draft, plannedEarlyEnd)

  return (
    <fieldset className={OPERATOR_FIELD_GROUP}>
      <legend className={`${OPERATOR_FIELDSET_LEGEND} flex items-center gap-1`}>
        Learning continuity
        <OperatorFaqTrigger
          topicId="continuity-choices"
          label="Open continuity guide"
        />
      </legend>
      <div className="grid gap-3 sm:grid-cols-3">
        <PolicySelect
          label="Placement"
          icon={<Icon name="placement" size={14} />}
          help="Require a new diagnostic, or reuse the student’s latest eligible result. Reusing placement does not by itself carry curriculum progress."
          value={draft.placementRequirement}
          values={['require_new_diagnostic', 'reuse_latest_result']}
          optionLabel={(value) =>
            value === 'reuse_latest_result' && reusablePlacementLevel !== null
              ? (
                  <>
                    Reuse latest result:{' '}
                    <strong className="font-semibold">
                      {reusablePlacementLevel}
                    </strong>
                  </>
                )
              : policyLabel(value)
          }
          disabledValues={
            reusablePlacementLevel === null ? ['reuse_latest_result'] : []
          }
          onChange={(placementRequirement) =>
            setDraft((current) => ({
              ...current,
              placementRequirement:
                placementRequirement as PlacementRequirement,
            }))
          }
        />
        <PolicySelect
          label="Curriculum"
          icon={<Icon name="program" size={14} />}
          help="Start a clean curriculum attempt, or continue compatible progress from a previous enrollment. Continuing curriculum automatically requires placement reuse."
          value={draft.curriculumProgressRequirement}
          values={['start_new_attempt', 'continue_latest_progress']}
          onChange={(curriculumProgressRequirement) =>
            setDraft((current) => ({
              ...current,
              curriculumProgressRequirement:
                curriculumProgressRequirement as CurriculumProgressRequirement,
              placementRequirement:
                curriculumProgressRequirement === 'continue_latest_progress'
                  ? 'reuse_latest_result'
                  : current.placementRequirement,
            }))
          }
        />
        <PolicySelect
          label="Trophies"
          icon={<Icon name="trophy" size={14} />}
          help="Start the new enrollment at zero trophies, or continue compatible trophy progress from a previous enrollment. The new enrollment keeps its own trophy goal either way."
          value={draft.trophyProgressRequirement}
          values={['start_new_progress', 'continue_latest_progress']}
          onChange={(trophyProgressRequirement) =>
            setDraft((current) => ({
              ...current,
              trophyProgressRequirement:
                trophyProgressRequirement as TrophyProgressRequirement,
            }))
          }
        />
      </div>
      {draft.scheduledStartOn !== '' && reusablePlacementLevel === null && (
        <p className="text-caption text-text-secondary">
          No earlier placement result is available before this start date, so
          a new diagnostic is required.
        </p>
      )}
      {carrying && (
        <div>
          <FieldLabel
            htmlFor="continuity-source"
            label="Previous enrollment to continue from"
            help="Choose the earlier enrollment whose compatible curriculum or trophy progress should continue here. It is reserved so two future enrollments cannot both continue from it."
          />
          <Select
            value={draft.sourceEnrollmentId}
            onValueChange={(sourceEnrollmentId) =>
              setDraft((current) => ({
                ...current,
                sourceEnrollmentId,
              }))
            }
          >
            <SelectTrigger id="continuity-source">
              <SelectValue placeholder="Choose an earlier enrollment" />
            </SelectTrigger>
            <SelectContent>
              {sourceOptions.map((source) => {
                const problem = continuitySourceProblem(
                  source,
                  draft,
                  plannedEarlyEnd,
                )
                const sourceEffectiveEnd =
                  plannedEarlyEnd?.enrollmentId === source.enrollmentId
                    ? plannedEarlyEnd.endedOn
                    : (source.endedOn ?? source.accessEndsOn)
                return (
                  <SelectItem
                    key={source.enrollmentId}
                    value={source.enrollmentId}
                    disabled={problem !== null}
                  >
                    {prettyDate(source.scheduledStartOn)} →{' '}
                    {prettyDate(sourceEffectiveEnd)} · {source.program?.name.en ?? 'Program unavailable'}
                    {problem !== null ? ` · incompatible` : ''}
                  </SelectItem>
                )
              })}
            </SelectContent>
          </Select>
          {selectedSourceProblem !== null && (
            <Notice variant="warning">{selectedSourceProblem}</Notice>
          )}
          {draft.scheduledStartOn !== '' &&
            sourceOptions.length > 0 &&
            sourceOptions.every(
              (source) =>
                continuitySourceProblem(source, draft, plannedEarlyEnd) !==
                null,
            ) && (
              <p className="mt-1 text-caption text-text-secondary">
                No earlier enrollment is compatible with these dates and
                program.
              </p>
            )}
        </div>
      )}
    </fieldset>
  )
}

function EnrollmentReview({
  draft,
  cohort,
  replacementSource,
}: {
  draft: DraftForm
  cohort: NonNullable<ReturnType<typeof useCohort>['data']> | null
  replacementSource: EnrollmentRow | null
}): JSX.Element {
  const company = draft.visibility === 'company'
  return (
    <div className="space-y-3 rounded-token-card bg-surface-app p-4">
      <p className="text-body">
        Nothing has changed yet. Confirm the complete period below.
      </p>
      <dl className="grid gap-2 text-body sm:grid-cols-2">
        <ReviewValue label="Visibility">
          {company ? `Company · ${cohort?.name ?? 'unknown cohort'}` : 'Private'}
        </ReviewValue>
        <ReviewValue
          label="Access window"
          icon={<Icon name="date" size={14} />}
        >
          {prettyDate(
            company
              ? (cohort?.scheduledStartOn ?? null)
              : draft.scheduledStartOn,
          )}{' '}
          →{' '}
          {prettyDate(
            company ? (cohort?.accessEndsOn ?? null) : draft.accessEndsOn,
          )}
        </ReviewValue>
        <ReviewValue
          label="Tier"
          icon={<Icon name="tier" size={14} />}
        >
          {company ? cohort?.defaultTier : draft.tier}
        </ReviewValue>
        <ReviewValue
          label="Program"
          icon={<Icon name="program" size={14} />}
        >
          <ProgramName
            programId={company ? cohort?.defaultProgramId : draft.programId}
            currentProgram={company ? cohort?.defaultProgram : null}
          />
        </ReviewValue>
        <ReviewValue label="Trophy goal">
          {company ? cohort?.defaultTrophyGoal : draft.trophyGoal}
        </ReviewValue>
        <ReviewValue
          label="Placement"
          icon={<Icon name="placement" size={14} />}
        >
          {policyLabel(
            company ? 'require_new_diagnostic' : draft.placementRequirement,
          )}
        </ReviewValue>
        <ReviewValue
          label="Curriculum"
          icon={<Icon name="program" size={14} />}
        >
          {policyLabel(
            company
              ? 'start_new_attempt'
              : draft.curriculumProgressRequirement,
          )}
        </ReviewValue>
        <ReviewValue
          label="Trophies"
          icon={<Icon name="trophy" size={14} />}
        >
          {policyLabel(
            company ? 'start_new_progress' : draft.trophyProgressRequirement,
          )}
        </ReviewValue>
        {replacementSource !== null && (
          <ReviewValue label="Reason">{draft.replacementReason}</ReviewValue>
        )}
        {!company && draft.externalPolicyRef.trim() !== '' && (
          <ReviewValue label="External policy reference">
            {draft.externalPolicyRef}
          </ReviewValue>
        )}
      </dl>
      {replacementSource !== null && (
        <Notice variant="warning">
          Confirming commits the old period&apos;s terminal change and the
          replacement together. If either fails, neither is saved.
        </Notice>
      )}
    </div>
  )
}

function ReviewValue({
  label,
  icon,
  children,
}: {
  label: string
  icon?: ReactNode
  children: ReactNode
}): JSX.Element {
  return (
    <div>
      <dt className="flex items-center gap-1.5 text-text-secondary">
        {icon}
        {label}
      </dt>
      <dd>{children ?? 'not set'}</dd>
    </div>
  )
}

function PolicySelect({
  label,
  icon,
  help,
  value,
  values,
  optionLabel = policyLabel,
  disabledValues = [],
  onChange,
}: {
  label: string
  icon: ReactNode
  help: string
  value: string
  values: string[]
  optionLabel?: (value: string) => ReactNode
  disabledValues?: string[]
  onChange: (value: string) => void
}): JSX.Element {
  return (
    <div>
      <FieldLabel
        label={label}
        icon={icon}
        size="compact"
        helpLabel={`${label} policy`}
        help={help}
      />
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger aria-label={label}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {values.map((option) => (
            <SelectItem
              key={option}
              value={option}
              disabled={disabledValues.includes(option)}
            >
              {optionLabel(option)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )
}
